import { useEffect } from 'react';
import useSWR from 'swr';
import { useAppState } from '../app/appContext';
import { listZones, type ZoneDto } from '../api/zones';

/** Shared SWR key — also used by BranchSelector. One request, shared cache. */
export const ZONES_SWR_KEY = 'zones:list:1000';

// Last-known zones list, persisted per browser (cleared on logout with the
// jwt) — same rationale as the /me identity cache: the branch selector and
// zone gating must not stump while the API is slow/briefly down. Live data
// replaces it as soon as the request lands.
const ZONES_CACHE_KEY = 'zones:last';
const liveZones = { loaded: false };

function readCachedZones(): ZoneDto[] | undefined {
  try {
    const raw = localStorage.getItem(ZONES_CACHE_KEY);
    const parsed = raw ? (JSON.parse(raw) as ZoneDto[]) : undefined;
    return Array.isArray(parsed) && parsed.length ? parsed : undefined;
  } catch {
    return undefined;
  }
}

async function fetchAllZones(): Promise<ZoneDto[]> {
  const resp = await listZones(1000);
  liveZones.loaded = true;
  try { localStorage.setItem(ZONES_CACHE_KEY, JSON.stringify(resp.data.results)); } catch {}
  return resp.data.results;
}

/**
 * Returns the currently selected zone and the full zones list.
 * Shares SWR cache with BranchSelector — no extra network requests.
 * Handles the number/string id discrepancy from the Django REST API.
 */
/**
 * The zones list with the shared options — the ONE way to read it.
 *
 * BranchSelector used to call useSWR on the same key with its own bare fetcher:
 * no cached fallback, no poll. SWR's fallbackData is per-hook, not shared, so on
 * a cold start of the installed app (Edem, 14.09) a single failed first request
 * turned the selector red — «Не удалось загрузить филиалы» — even though the
 * last session's list sat in localStorage. The proxy log showed why the first
 * request fails: 7 client-aborted (499, up to the 30 s axios timeout) and 2
 * 502s on /core/api/zones/ in 12 h, several from iCloud Private Relay (iPhone).
 */
export function useZonesList() {
  const swr = useSWR<ZoneDto[]>(ZONES_SWR_KEY, fetchAllZones, {
    // Cached fallback + poll-until-live: after a transient API outage (deploy
    // window / starved workers) the default exponential error-backoff could
    // leave the branch selector empty for minutes. The last session's list
    // renders instantly; poll every 15s until a LIVE response replaces it.
    fallbackData: readCachedZones(),
    refreshInterval: () => (liveZones.loaded ? 0 : 15_000),
    // A failed first request retries in 2 s, not SWR's default 5 s-and-growing:
    // on app launch the first fetch is the one most likely to be cut off.
    errorRetryInterval: 2_000,
    // No focus-revalidation: refetching the zone list on window refocus gave
    // every consumer (incl. AnalyticsPage) a new `zones` array → re-render →
    // the chart/photo panels "плясали" on refocus (Alexey 2026-07-21). The list
    // is kept live by the poll above + explicit mutate, so focus refetch is
    // pure churn here.
    revalidateOnFocus: false,
  });

  // …but while NO live list has landed, coming back to the app IS the moment to
  // ask again: iOS suspends a backgrounded PWA and kills its in-flight request,
  // and the 15 s poll would otherwise leave the error up after the resume.
  const { mutate } = swr;
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !liveZones.loaded) void mutate();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mutate]);

  return swr;
}

export function useCurrentZone() {
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId; // string | null (as stored via setZone)

  const { data: zones = [], mutate } = useZonesList();

  const currentZone = zoneId
    ? (zones.find(z => String(z.id) === String(zoneId)) ?? null)
    : null;

  // A zone id that is set (usually pasted/shared as a URL) but absent from the
  // user's own list means "not yours" — distinct from "still loading", which
  // also has currentZone === null. Only assert it once a LIVE list has landed:
  // the localStorage fallback belongs to whoever used this browser last, so
  // judging access from it could deny a branch the current user actually has
  // (Alexey 09.09 — following a link to someone else's spot showed a generic
  // load failure instead of an honest "no access").
  const noZoneAccess = Boolean(zoneId) && liveZones.loaded && currentZone === null;

  return { zoneId, currentZone, zones, mutate, noZoneAccess };
}
